import { Button } from '@/components/shadcn/button'
import { Label } from '@/components/shadcn/label'
import { MultiSelectCombobox, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { useRestClient } from '@/contexts/QueryProvider'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  ApplicationAccessBlock,
  assignmentsFromPeople,
  formatUserOrNominee,
  personKey,
  uniquePeople,
  userLabelFromOptions,
  UserOrNominee,
} from '../../Requests/types'
import { ApplicationAssignmentRow } from './RequestFormFields'

type LinkedApplicationsFieldProps = {
  applicationOptions: SelectOption<number>[]
  value: ApplicationAccessBlock[]
  onChange: (value: ApplicationAccessBlock[]) => void
  userOptions: SelectOption<number>[]
  organisationId?: number
  allowNominee?: boolean
  assignmentPeople?: UserOrNominee[]
}

export function LinkedApplicationsField({
  applicationOptions,
  value,
  onChange,
  userOptions,
  allowNominee = true,
  assignmentPeople,
}: LinkedApplicationsFieldProps) {
  const { getApplicationsApiListApplicationRoles } = useRestClient()
  const peopleMode = assignmentPeople != null
  const sourcePeople = useMemo(() => uniquePeople(assignmentPeople ?? []), [assignmentPeople])

  const selectedApplicationIds = useMemo(() => value.map((block) => block.applicationId), [value])
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (!peopleMode) return

    const next = value.map((block) => ({
      ...block,
      assignments: assignmentsFromPeople(sourcePeople, block.assignments),
    }))

    const unchanged = next.every(
      (block, index) => JSON.stringify(block.assignments) === JSON.stringify(value[index]?.assignments)
    )
    if (unchanged) return
    onChangeRef.current(next)
  }, [peopleMode, sourcePeople, value])

  const handleApplicationSelectionChange = useCallback(
    (applicationIds: number[]) => {
      const next = applicationIds.map((applicationId) => {
        const existing = value.find((block) => block.applicationId === applicationId)
        if (existing) {
          return peopleMode
            ? { ...existing, assignments: assignmentsFromPeople(sourcePeople, existing.assignments) }
            : existing
        }
        return {
          applicationId,
          assignments: peopleMode ? assignmentsFromPeople(sourcePeople, []) : [],
        }
      })
      onChange(next)
    },
    [onChange, peopleMode, sourcePeople, value]
  )

  const updateBlock = useCallback(
    (applicationId: number, block: ApplicationAccessBlock) => {
      onChange(value.map((entry) => (entry.applicationId === applicationId ? block : entry)))
    },
    [onChange, value]
  )

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Linked applications *</Label>
        <MultiSelectCombobox
          options={applicationOptions}
          selectedValues={selectedApplicationIds}
          onSelectionChange={handleApplicationSelectionChange}
          placeholder="Select applications..."
          searchPlaceholder="Search applications..."
          renderBadgesInTrigger
        />
      </div>
      {peopleMode && sourcePeople.length === 0 && value.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Add project leaders, administrators, or members above. Their application roles can then be assigned here.
        </p>
      )}
      {peopleMode && sourcePeople.length > 0 && value.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Assign roles to the nominated project people. Leave roles empty to skip application access for that person.
        </p>
      )}
      {value.map((block) => (
        <ApplicationBlockEditor
          key={block.applicationId}
          block={block}
          applicationLabel={
            applicationOptions.find((option) => option.value === block.applicationId)?.label ??
            `Application #${block.applicationId}`
          }
          userOptions={userOptions}
          allowNominee={allowNominee}
          peopleMode={peopleMode}
          onChange={(updated) => updateBlock(block.applicationId, updated)}
          getApplicationsApiListApplicationRoles={getApplicationsApiListApplicationRoles}
        />
      ))}
    </div>
  )
}

type ApplicationBlockEditorProps = {
  block: ApplicationAccessBlock
  applicationLabel: string
  userOptions: SelectOption<number>[]
  allowNominee: boolean
  peopleMode: boolean
  onChange: (block: ApplicationAccessBlock) => void
  getApplicationsApiListApplicationRoles: ReturnType<typeof useRestClient>['getApplicationsApiListApplicationRoles']
}

function ApplicationBlockEditor({
  block,
  applicationLabel,
  userOptions,
  allowNominee,
  peopleMode,
  onChange,
  getApplicationsApiListApplicationRoles,
}: ApplicationBlockEditorProps) {
  const { data: roles } = getApplicationsApiListApplicationRoles.useQuery({
    query: { applicationId: block.applicationId },
  })

  const roleOptions = useMemo<SelectOption<number>[]>(
    () => roles?.map((role) => ({ value: role.id!, label: role.name ?? `Role ${role.id}` })) ?? [],
    [roles]
  )

  const resolveUserLabel = useCallback(
    (entry: UserOrNominee) => userLabelFromOptions(entry.userId, userOptions),
    [userOptions]
  )

  const addAssignment = () => {
    onChange({
      ...block,
      assignments: [...block.assignments, { roleIds: [] }],
    })
  }

  const updateAssignment = (index: number, assignment: ApplicationAccessBlock['assignments'][number]) => {
    const assignments = [...block.assignments]
    assignments[index] = assignment
    onChange({ ...block, assignments })
  }

  const removeAssignment = (index: number) => {
    onChange({
      ...block,
      assignments: block.assignments.filter((_, i) => i !== index),
    })
  }

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="font-medium">{applicationLabel}</div>
      {block.assignments.map((assignment, index) =>
        peopleMode ? (
          <div key={`${block.applicationId}-person-${personKey(assignment) ?? index}`} className="space-y-2 rounded-md border p-3">
            <div className="text-sm font-medium">{formatUserOrNominee(assignment, resolveUserLabel(assignment))}</div>
            <MultiSelectCombobox
              options={roleOptions}
              selectedValues={assignment.roleIds}
              onSelectionChange={(roleIds) => updateAssignment(index, { ...assignment, roleIds })}
              placeholder="Select roles..."
              searchPlaceholder="Search roles..."
              renderBadgesInTrigger
            />
          </div>
        ) : (
          <ApplicationAssignmentRow
            key={`${block.applicationId}-assignment-${index}`}
            userOptions={userOptions}
            roleOptions={roleOptions}
            assignment={assignment}
            onChange={(updated) => updateAssignment(index, updated)}
            onRemove={() => removeAssignment(index)}
            allowNominee={allowNominee}
          />
        )
      )}
      {!peopleMode && (
        <Button type="button" variant="outline" size="sm" onClick={addAssignment}>
          Add user / roles
        </Button>
      )}
    </div>
  )
}
